"use client";

import {
  submitContactRequest,
  type SubmitContactRequestState,
} from "@/actions/contact/submitContactRequest";
import { Checkbox, Input, Select, TextArea } from "@/components/shared/form";
import { Button, Dialog, TurnstileWidget } from "@/components/shared/public";
import { MessageSquareText } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useActionState, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const initialState: SubmitContactRequestState | null = null;

const ContactForm = () => {
  const t = useTranslations("common");
  const tP = useTranslations("order.placeholders");
  const locale = useLocale();
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

  const [state, formAction, isPending] = useActionState(
    submitContactRequest,
    initialState,
  );

  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);
  const [openedAt, setOpenedAt] = useState("");
  const [consent, setConsent] = useState(false);
  const [salutation, setSalutation] = useState("");
  const formRef = useRef<HTMLFormElement | null>(null);
  const submittedValuesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    setOpenedAt(Date.now().toString());
  }, []);

  useEffect(() => {
    if (!state) return;
    if (state.success) {
      const thankYouPath =
        locale === "de" ? "/kontakt/danke" : `/${locale}/kontakt/danke`;
      window.location.assign(thankYouPath);
      return;
    } else {
      restoreSubmittedValues();
      if (!state.fieldErrors) {
        toast.error(state.message);
      }
    }
    // Cloudflare Turnstile burns the token on every verify call regardless
    // of outcome — always re-issue so a 422 with field errors doesn't trap
    // the user on a stale single-use token.
    setToken("");
    setResetSignal((s) => s + 1);
  }, [locale, state]);

  const fieldError = (key: string): string =>
    state?.fieldErrors?.[key]?.[0] ?? "";

  const captureSubmittedValues = () => {
    if (!formRef.current) return;

    const values: Record<string, string> = {};
    const formData = new FormData(formRef.current);
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        values[key] = value;
      }
    });
    submittedValuesRef.current = values;
  };

  const restoreSubmittedValues = () => {
    const form = formRef.current;
    if (!form) return;

    const values = submittedValuesRef.current;
    const restoreField = (name: string) => {
      const field = form.elements.namedItem(name);
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLTextAreaElement
      ) {
        field.value = values[name] ?? "";
      }
    };

    ["first_name", "last_name", "company", "phone", "email", "message"].forEach(
      restoreField,
    );
    setSalutation(values.salutation ?? "");
    setConsent(values.consent_accepted === "1");
  };

  return (
    <>
      <Dialog
        trigger={
          <span
            data-testid="contact-form-trigger"
            className="bg-primary border-primary lg:hover:bg-background lg:hover:text-primary fixed bottom-0 left-28 z-50 m-6 flex h-12 cursor-pointer items-center justify-center rounded-none border-2 px-5 py-3 text-lg text-white shadow-lg transition-all duration-300 sm:left-0 sm:m-8"
          >
            <span className="flex items-center gap-2 font-medium">
              <MessageSquareText className="size-5" />
              {t("contact")}
            </span>
          </span>
        }
        title={t("contact")}
        description={t("contactDescription")}
        className="sm:max-w-2xl"
      >
        <form
          ref={formRef}
          action={formAction}
          onSubmit={captureSubmittedValues}
          className="flex w-full flex-col gap-3"
        >
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            defaultValue=""
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />
          <input type="hidden" name="_form_open_at" value={openedAt} readOnly />
          <input
            type="hidden"
            name="cf-turnstile-response"
            value={token}
            readOnly
          />
          <input
            type="hidden"
            name="consent_accepted"
            value={consent ? "1" : ""}
            readOnly
          />
          <Select
            selectItem={
              [
                { label: t("mr"), value: "mr" },
                { label: t("mrs"), value: "mrs" },
                { label: t("divers"), value: "divers" },
                { label: t("noneSalutation"), value: "noneSalutation" },
              ] as const
            }
            name="salutation"
            value={salutation}
            onValueChange={setSalutation}
            placeholder={t("pleaseSelect")}
            label={`${t("salutation")}*`}
            error={fieldError("salutation")}
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              name="first_name"
              label={`${t("firstName")}*`}
              placeholder={tP("exampleFirstNameShort")}
              error={fieldError("first_name")}
            />
            <Input
              name="last_name"
              label={`${t("lastName")}*`}
              placeholder={tP("exampleLastNameShort")}
              error={fieldError("last_name")}
            />
          </div>
          <Input
            name="company"
            label={t("company")}
            placeholder={tP("exampleCompanyFull")}
            error={fieldError("company")}
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              name="phone"
              label={t("phone")}
              placeholder="+49 123 4567890"
              error={fieldError("phone")}
            />
            <Input
              name="email"
              label={`${t("email")}*`}
              placeholder="max.mustermann@example.com"
              error={fieldError("email")}
            />
          </div>
          <TextArea
            name="message"
            label={`${t("message")}*`}
            placeholder={`${t("yourMessage")}...`}
            error={fieldError("message")}
          />
          <Input
            className="cursor-pointer"
            name="attachment"
            label={t("uploadFile")}
            type="file"
            error={fieldError("attachment")}
          />
          <Checkbox
            label={`${t("acceptTerms")}*`}
            name="consent_accepted_ui"
            checked={consent}
            error={fieldError("consent_accepted")}
            onCheckedChange={(value) => setConsent(value === true)}
          />
          {turnstileSiteKey ? (
            <TurnstileWidget
              siteKey={turnstileSiteKey}
              onToken={setToken}
              onExpire={() => setToken("")}
              onError={() => setToken("")}
              resetSignal={resetSignal}
            />
          ) : null}
          <div className="flex w-full items-center justify-end pt-2">
            <Button
              type="submit"
              className="w-full sm:w-auto"
              title={isPending ? t("submitting") : t("submit")}
              disabled={isPending || (turnstileSiteKey ? !token : false)}
            />
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default ContactForm;
